import React, { useEffect, useRef, useState } from 'react';
import './Budgets.css';
import { Link } from 'react-router-dom';
import privateApi from "../../apis/privateApi";
import { useSelector } from 'react-redux';
const Budgets = () => {
    const role = useSelector(state => state.role.role);

    const [scope, setScope] = useState('');
    const [budgets, setBudgets] = useState([]);
    const [entities, setEntities] = useState([]);
    const [entitySearch, setEntitySearch] = useState('');
    const [selectedEntities, setSelectedEntities] = useState([]);
    const [filterStatus, setFilterStatus] = useState('all');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showEntities, setShowEntities] = useState(false);
    const dropRef = useRef(null);

    const filteredEntities = entities.filter(e => e.name.toLowerCase().includes(entitySearch.toLowerCase()));

    /* close dropdown on outside click */
    useEffect(() => {
        const h = e => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowEntities(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    /* Fetch entity list */
    useEffect(() => {
        if (scope !== 'state' && scope !== 'lga') {
            setBudgets([]);
            return;
        };
        (async () => {
            try {
                setLoading(true);
                const { data } = await privateApi.get(`/api/${scope}/budgets/locations`);
                setEntities(data.data || []);
                setSelectedEntities([]);
                setError('');
            } catch { setError(`Failed to fetch ${scope}s`); }
            finally { setLoading(false); }
        })();
    }, [scope]);

    /* Fetch budgets */
    useEffect(() => {
        if (scope !== 'state' && scope !== 'lga') return;
        (async () => {
            try {
                setLoading(true);
                let url = `/api/${scope}/budgets?level=${scope}`;
                if (filterStatus !== 'all') url += `&status=${filterStatus}`;
                if (selectedEntities.length > 0) url += `&${scope}Ids=${selectedEntities.join(',')}`;
                const { data } = await privateApi.get(url);
                setBudgets(data.data || []);
                setError('');
            } catch (err) {
                setError(err.response?.data?.message || `Failed to fetch ${scope} budgets.`);
            } finally { setLoading(false); }
        })();
    }, [scope, filterStatus, selectedEntities]);

    const toggleEntity = id => setSelectedEntities(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    const allFilteredSelected = filteredEntities.length > 0 && filteredEntities.every(e => selectedEntities.includes(e._id));
    const handleSelectAll = checked => {
        const ids = filteredEntities.map(e => e._id);
        setSelectedEntities(p => checked ? [...new Set([...p, ...ids])] : p.filter(id => !ids.includes(id)));
    };

    const selCount = selectedEntities.length;

    return (
        <div className="bgs-page">
            {/* Header */}
            <div className="bgs-header">
                <div>
                    <h1>{scope.charAt(0).toUpperCase() + scope.slice(1)} Budgets</h1>
                    <p>Manage and track government budgets by fiscal year</p>
                </div>
                <Link to="/budgets/create" className="bgs-table-btn view" style={{ padding: '9px 18px', fontSize: 13 }}>
                    + Create Budget
                </Link>
            </div>

            {/* Toolbar */}
            <div className="bgs-toolbar">
                <span className="bgs-filter-label">Filter</span>

                {/* Scope */}
                <select className="bgs-select" value={scope} onChange={e => setScope(e.target.value)}>
                    <option value="">Select Scope</option>
                    {(role.accessScope === 'all' || role.accessScope === 'state') && <option value="state">State</option>}
                    {(role.accessScope === 'all' || role.accessScope === 'state' || role.accessScope === 'lga') && <option value="lga">LGA</option>}
                </select>

                {/* Entity dropdown */}
                <div className="bgs-entity-wrap" ref={dropRef}>
                    <button
                        className={`bgs-entity-btn ${showEntities ? "open" : ""} ${selCount ? "has-sel" : ""}`}
                        onClick={() => setShowEntities(p => !p)}
                    >
                        Filter by {scope}
                        {selCount > 0 && <span className="bgs-entity-count">{selCount}</span>}
                        <span className="bgs-entity-chevron">▼</span>
                    </button>

                    {showEntities && (
                        <div className="bgs-entity-panel">
                            <div className="bgs-entity-search-wrap">
                                <input className="bgs-entity-search" autoFocus
                                    placeholder={`Search ${scope}s…`}
                                    value={entitySearch}
                                    onChange={e => setEntitySearch(e.target.value)} />
                            </div>
                            <div className="bgs-entity-bar">
                                <label className="bgs-select-all-label">
                                    <input type="checkbox" checked={allFilteredSelected}
                                        onChange={e => handleSelectAll(e.target.checked)} />
                                    Select All
                                </label>
                                <button className="bgs-clear-btn"
                                    onClick={() => setSelectedEntities(p => p.filter(id => !filteredEntities.map(e => e._id).includes(id)))}>
                                    Clear
                                </button>
                            </div>
                            <div className="bgs-entity-list">
                                {filteredEntities.length === 0
                                    ? <div className="bgs-entity-empty">No {scope} found</div>
                                    : filteredEntities.map(e => (
                                        <label key={e._id} className="bgs-entity-item">
                                            <input type="checkbox" checked={selectedEntities.includes(e._id)}
                                                onChange={() => toggleEntity(e._id)} />
                                            {e.name}
                                        </label>
                                    ))
                                }
                            </div>
                        </div>
                    )}
                </div>

                {/* Status */}
                <select className="bgs-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                    <option value="all">All Statuses</option>
                    <option value="draft">Draft</option>
                    <option value="approved">Approved</option>
                    <option value="locked">Locked</option>
                </select>

                <div className="bgs-toolbar-spacer" />
            </div>

            {/* Table */}
            <div className="bgs-table-card">
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: 14 }}>
                        <div style={{ width: 40, height: 40, border: '3px solid #D2DDD5', borderTopColor: '#006B3C', borderRadius: '50%', animation: 'bgs-spin .8s linear infinite' }} />
                        <p style={{ fontSize: 13, color: '#6B7F70', fontWeight: 500 }}>Loading {scope} Budgets…</p>
                        <style>{`@keyframes bgs-spin { to { transform:rotate(360deg) } }`}</style>
                    </div>
                ) : error ? (
                    <div className="bgs-error">⚠ {error}</div>
                ) : budgets.length === 0 ? (
                    <div className="bgs-empty"><span className="bgs-empty-icon">💰</span>No budgets found.</div>
                ) : (
                    <div className="bgs-table-scroll">
                        <table className="bgs-table">
                            <thead>
                                <tr>
                                    <th>{scope.toUpperCase()}</th>
                                    <th>Year</th><th>Type</th><th>Revision</th>
                                    <th>Status</th><th>Remark</th>
                                    <th>Created By</th><th>Approved By</th><th>Locked By</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {budgets.map(budget => (
                                    <tr key={budget._id}>
                                        <td>{budget[`${scope}Id`]?.name || '—'}</td>
                                        <td>{budget.year}</td>
                                        <td>{budget.type}</td>
                                        <td>{budget.revisionNumber}</td>
                                        <td>
                                            <span className={`bgs-status ${budget.status}`}>
                                                <span className="bgs-status-dot" />
                                                {budget.status}
                                            </span>
                                        </td>
                                        <td>{budget.remark || '—'}</td>
                                        <td>{budget.createdBy?.name || '—'}</td>
                                        <td>{budget.approvedBy?.name || '—'}</td>
                                        <td>{budget.lockedBy?.name || '—'}</td>
                                        <td>
                                            <div className="bgs-table-actions">
                                                <Link className="bgs-table-btn view"
                                                    to="/budgets/detail" state={{ budgetId: budget._id, scope: budget.level }}>
                                                    View
                                                </Link>
                                                {budget.status === 'draft' && (
                                                    <Link className="bgs-table-btn edit"
                                                        to="/budgets/edit" state={{ budgetId: budget._id, scope: budget.level }}>
                                                        Edit
                                                    </Link>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Budgets;